import { Badge } from '@heidi/design-system/badge';
import { Globe, MapPinned } from 'lucide-react';
import { FormattedMessage } from 'react-intl';

import type { Jurisdiction } from '../../contracts';

import type { JSX } from 'react';

interface JurisdictionBadgeProps {
  jurisdiction: Jurisdiction;
}

/**
 * Tri-state jurisdiction chip for a citation source (EV-022 risk control).
 *
 *   - `inRegion`     → neutral "In region" chip (in-jurisdiction).
 *   - `global`       → neutral "Global" chip (global/world source).
 *   - `outOfRegion`  → neutral generic "Out of region" chip (not
 *                       `jurisdiction.label`) so clinicians see
 *                       out-of-jurisdiction guidance without implying a
 *                       specific country name in the chip text (EV-022).
 *
 * Vocabulary mirrors the backend `regional_relevance` wire field
 * (`service/chat/source_region_matching.py`); the BE wire value, the
 * camel-case UI state, and the chip text all read the same way
 * (`in_region` / `inRegion` / "In region").
 *
 * Guideline status is rendered separately by the caller; this badge only
 * conveys geographic relevance.
 */
export function JurisdictionBadge({ jurisdiction }: JurisdictionBadgeProps): JSX.Element {
  if (jurisdiction.state === 'global') {
    return (
      <Badge color="neutral" size="sm" data-testid="jurisdiction-badge">
        <Globe />
        <FormattedMessage
          defaultMessage="Global"
          id="l6JjWj4HIv"
          description="Chip marking a citation source as applying globally rather than to the clinician’s region"
        />
      </Badge>
    );
  }

  if (jurisdiction.state === 'outOfRegion') {
    return (
      <Badge color="neutral" size="sm" data-testid="jurisdiction-badge">
        <MapPinned />
        <FormattedMessage
          defaultMessage="Out of region"
          id="UkM6bu9vwY"
          description="Chip marking a citation source from outside the clinician’s region"
        />
      </Badge>
    );
  }

  return (
    <Badge color="neutral" size="sm" data-testid="jurisdiction-badge">
      <MapPinned />
      <FormattedMessage
        defaultMessage="In region"
        id="p2SsoSSkiZ"
        description="Chip marking a citation source from within the clinician’s region"
      />
    </Badge>
  );
}
